import { gql } from '@apollo/client';
import { useMutation } from '@apollo/client/react';
import { useState } from 'react';
import { toast } from 'sonner';
import { z } from 'zod';

import { EditDialog } from '../../../EditDialog';
import { EditFormField } from '../../../EditFormField';
import {
  type AccountOthersData,
  GET_ACCOUNT_OTHERS
} from '../queries/accountOthersQuery';

interface FieldMapping {
  apiField: string;
  formField: keyof AccountOthersData;
  isDate?: boolean;
}

const FINANCIAL_FIELDS: FieldMapping[] = [
  { apiField: 'commitmentAmount', formField: 'commitmentAmount' },
  { apiField: 'commitmentAmountBase', formField: 'commitmentAmountBase' },
  {
    apiField: 'lpInvestmentAmmountMin',
    formField: 'lpInvestmentAmountMin'
  },
  {
    apiField: 'lpInvestmentAmmountMinBase',
    formField: 'lpInvestmentAmountMinBase'
  },
  {
    apiField: 'lpInvestmentAmmountMax',
    formField: 'lpInvestmentAmountMax'
  },
  {
    apiField: 'lpInvestmentAmmountMaxBase',
    formField: 'lpInvestmentAmountMaxBase'
  },
  {
    apiField: 'assetsUnderManagement',
    formField: 'assetsUnderManagement'
  },
  {
    apiField: 'assetsUnderManagementBase',
    formField: 'assetsUnderManagementBase'
  },
  { apiField: 'annualRevenue', formField: 'annualRevenue' },
  { apiField: 'annualRevenueBase', formField: 'annualRevenueBase' },
  { apiField: 'openRevenue', formField: 'openRevenue' },
  { apiField: 'openRevenueBase', formField: 'openRevenueBase' },
  {
    apiField: 'openRevenueLastUpdatedTime',
    formField: 'openRevenueLastUpdatedTime',
    isDate: true
  }
];

function getChangedValues(
  fields: FieldMapping[],
  current: AccountOthersData,
  initial: AccountOthersData
): Record<string, null | number | string> {
  const changes: Record<string, null | number | string> = {};

  for (const { apiField, formField, isDate } of fields) {
    const currentVal = current[formField];
    const initialVal = initial[formField];

    const hasChanged = isDate
      ? toDateInputValue(currentVal as string) !==
        toDateInputValue(initialVal as string)
      : currentVal !== initialVal;

    if (hasChanged) {
      if (isDate) {
        changes[apiField] = currentVal === '' ? null : (currentVal as string);
      } else {
        changes[apiField] = parseCurrencyToNumber(currentVal as string);
      }
    }
  }

  return changes;
}

function parseCurrencyToNumber(value: string): null | number {
  if (!value || value === '-') return null;
  const cleaned = value.replace(/[$,]/g, '');
  const num = parseFloat(cleaned);
  return isNaN(num) ? null : num;
}

function toDateInputValue(isoDate: string): string {
  if (!isoDate) return '';
  const date = new Date(isoDate);
  return isNaN(date.getTime()) ? isoDate : date.toISOString().split('T')[0];
}

// Validation schema for currency/number fields
const numberSchema = z.string().refine(
  val => {
    if (val === '' || val === '-') return true;
    const cleaned = val.replace(/[$,]/g, '');
    const num = parseFloat(cleaned);
    return !isNaN(num);
  },
  {
    message: 'Please enter a valid number'
  }
);

const CURRENCY_FIELDS = [
  'commitmentAmount',
  'commitmentAmountBase',
  'lpInvestmentAmountMin',
  'lpInvestmentAmountMinBase',
  'lpInvestmentAmountMax',
  'lpInvestmentAmountMaxBase',
  'assetsUnderManagement',
  'assetsUnderManagementBase',
  'annualRevenue',
  'annualRevenueBase',
  'openRevenue',
  'openRevenueBase'
] as const;

const UPDATE_ACCOUNT = gql`
  mutation UpdateAccount($id: ID!, $values: AccountInput!) {
    updateAccount(id: $id, values: $values) {
      ok
      account {
        id
        commitmentAmount
        commitmentAmountBase
        lpInvestmentAmmountMin
        lpInvestmentAmmountMinBase
        lpInvestmentAmmountMax
        lpInvestmentAmmountMaxBase
        assetsUnderManagement
        assetsUnderManagementBase
        annualRevenue
        annualRevenueBase
        openRevenue
        openRevenueBase
        openRevenueLastUpdatedTime
      }
    }
  }
`;

export interface EditFinancialInfoDialogProps {
  accountId: string;
  initialValue: AccountOthersData;
  isOpen: boolean;
  onChange: (data: AccountOthersData) => void;
  onOpenChange: (open: boolean) => void;
  onSave: () => void;
  value: AccountOthersData;
}

export function EditFinancialInfoDialog({
  accountId,
  initialValue,
  isOpen,
  onChange,
  onOpenChange,
  onSave,
  value
}: EditFinancialInfoDialogProps) {
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSaving, setIsSaving] = useState(false);

  const [updateAccount] = useMutation(UPDATE_ACCOUNT, {
    refetchQueries: [
      { query: GET_ACCOUNT_OTHERS, variables: { id: accountId } }
    ]
  });

  const changes = getChangedValues(FINANCIAL_FIELDS, value, initialValue);
  const isDirty = Object.keys(changes).length > 0;

  const validateField = (field: string, fieldValue: string) => {
    if (CURRENCY_FIELDS.includes(field as (typeof CURRENCY_FIELDS)[number])) {
      const result = numberSchema.safeParse(fieldValue);
      if (!result.success && result.error.issues.length > 0) {
        setErrors(prev => ({
          ...prev,
          [field]: result.error.issues[0]?.message ?? 'Invalid number'
        }));
      } else {
        setErrors(prev => {
          const newErrors = { ...prev };
          // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
          delete newErrors[field];
          return newErrors;
        });
      }
    }
  };

  const updateField = <K extends keyof AccountOthersData>(
    field: K,
    fieldValue: AccountOthersData[K]
  ) => {
    if (CURRENCY_FIELDS.includes(field as (typeof CURRENCY_FIELDS)[number])) {
      validateField(field as string, fieldValue as string);
    }
    onChange({ ...value, [field]: fieldValue });
  };

  const handleSave = async () => {
    // Validate all currency fields before saving
    const newErrors: Record<string, string> = {};
    for (const field of CURRENCY_FIELDS) {
      const fieldValue = value[field];
      const result = numberSchema.safeParse(fieldValue);
      if (!result.success && result.error.issues.length > 0) {
        newErrors[field] = result.error.issues[0]?.message ?? 'Invalid number';
      }
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    if (!isDirty) {
      onSave();
      return;
    }

    setIsSaving(true);

    try {
      const response = await updateAccount({
        variables: { id: accountId, values: changes }
      });

      const data = response.data as {
        updateAccount?: { ok?: boolean };
      };

      if (data.updateAccount?.ok === false) {
        toast.error('Failed to save financial info');
      } else {
        setErrors({});
        toast.success('Financial info saved successfully');
        onSave();
      }
    } catch (error) {
      console.error('Error saving financial info:', error);
      toast.error('Failed to save financial info');
    } finally {
      setIsSaving(false);
    }
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setErrors({});
    }
    onOpenChange(open);
  };

  return (
    <EditDialog
      isDirty={isDirty}
      isOpen={isOpen}
      isSaving={isSaving}
      onOpenChange={handleOpenChange}
      onSave={handleSave}
      title="Edit Financial Info"
    >
      <div className="grid grid-cols-2 gap-6">
        <EditFormField
          error={errors.commitmentAmount}
          label="Commitment Amount"
          onChange={v => {
            updateField('commitmentAmount', v);
          }}
          value={value.commitmentAmount}
        />
        <EditFormField
          error={errors.commitmentAmountBase}
          label="Commitment Amount (Base)"
          onChange={v => {
            updateField('commitmentAmountBase', v);
          }}
          value={value.commitmentAmountBase}
        />
        <EditFormField
          error={errors.lpInvestmentAmountMin}
          label="LP Investment Amount (Min)"
          onChange={v => {
            updateField('lpInvestmentAmountMin', v);
          }}
          value={value.lpInvestmentAmountMin}
        />
        <EditFormField
          error={errors.lpInvestmentAmountMinBase}
          label="LP Investment Amount (Min) (Base)"
          onChange={v => {
            updateField('lpInvestmentAmountMinBase', v);
          }}
          value={value.lpInvestmentAmountMinBase}
        />
        <EditFormField
          error={errors.lpInvestmentAmountMax}
          label="LP Investment Amount (Max)"
          onChange={v => {
            updateField('lpInvestmentAmountMax', v);
          }}
          value={value.lpInvestmentAmountMax}
        />
        <EditFormField
          error={errors.lpInvestmentAmountMaxBase}
          label="LP Investment Amount (Max) (Base)"
          onChange={v => {
            updateField('lpInvestmentAmountMaxBase', v);
          }}
          value={value.lpInvestmentAmountMaxBase}
        />
        <EditFormField
          error={errors.assetsUnderManagement}
          label="Assets Under Management ($ mm)"
          onChange={v => {
            updateField('assetsUnderManagement', v);
          }}
          value={value.assetsUnderManagement}
        />
        <EditFormField
          error={errors.assetsUnderManagementBase}
          label="Assets Under Management ($ mm) (Base)"
          onChange={v => {
            updateField('assetsUnderManagementBase', v);
          }}
          value={value.assetsUnderManagementBase}
        />
        <EditFormField
          error={errors.annualRevenue}
          label="Annual Revenue"
          onChange={v => {
            updateField('annualRevenue', v);
          }}
          value={value.annualRevenue}
        />
        <EditFormField
          error={errors.annualRevenueBase}
          label="Annual Revenue (Base)"
          onChange={v => {
            updateField('annualRevenueBase', v);
          }}
          value={value.annualRevenueBase}
        />
        <EditFormField
          error={errors.openRevenue}
          label="Open Revenue"
          onChange={v => {
            updateField('openRevenue', v);
          }}
          value={value.openRevenue}
        />
        <EditFormField
          error={errors.openRevenueBase}
          label="Open Revenue (Base)"
          onChange={v => {
            updateField('openRevenueBase', v);
          }}
          value={value.openRevenueBase}
        />
        <EditFormField
          label="Open Revenue (Last Updated Time)"
          onChange={v => {
            updateField('openRevenueLastUpdatedTime', v);
          }}
          type="date"
          value={toDateInputValue(value.openRevenueLastUpdatedTime)}
        />
      </div>
    </EditDialog>
  );
}
